import { css } from "@emotion/css";
import { borderWidth, borderRadius, space, themeTokens } from "@octopusdeploy/design-system-tokens";
import * as React from "react";
import type { PopoverBasicHelpProps } from "../../Popover";
import { FormDescription } from "../Primitives/FormDescription";
import { InputLabel } from "../Primitives/InputLabel";
import { RadioGroupContext } from "./RadioGroupNew";
export interface RadioButtonProps<T extends string | boolean> {
    /**
     * The value of the radio button.
     */
    value: T;
    /**
     * The name of the radio group. If not provided, will use the name from RadioGroup context.
     */
    name: string;
    /**
     * The label of the radio button.
     */
    label: string;
    /**
     * Controls whether the radio button should be the default selected option.
     */
    hasDefaultMarker?: boolean;
    /**
     * Controls whether the radio button is disabled.
     */
    disabled?: boolean;
    /**
     * Controls whether the radio button should be automatically focused.
     */
    autoFocus?: boolean;
    /**
     * The description of the radio button.
     */
    description?: string;
    /**
     * PopoverBasicHelp component to display additional help information
     * displays the Information icon next to the group label
     */
    popover?: React.ReactElement<PopoverBasicHelpProps>;
}
/**
 * RadioButton component that supports rich descriptions with inline links.
 *
 * @example
 * ```tsx
 * import { RadioButtonNew } from '@octopusdeploy/design-system-components';
 *
 * <RadioButtonNew label="Label for radio button" description="Description for radio button" disabled={false} value="option1" name="radio-group"} />
 * ```
 */
export function RadioButtonNew<T extends string | boolean>({ label, description, disabled, value, name, autoFocus, hasDefaultMarker, popover }: RadioButtonProps<T>) {
    const context = React.useContext(RadioGroupContext);
    const handleChange = React.useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
        if (event.target.checked && context?.onChange) {
            context.onChange(value);
        }
    }, [context, value]);
    const checked = context?.value === value;
    const isDisabled = disabled || context?.disabled || false;
    // TODO: React18 has a useID hook we can use here instead.
    const inputId = React.useMemo(() => `radio-${name}-${Math.random().toString(36).substring(2, 9)}`, [name]);
    return (<div className={radioButtonStyles}>
            <input type="radio" name={name} value={`${value}`} disabled={isDisabled} className={radioInputStyles} checked={checked} onChange={handleChange} id={inputId} autoFocus={autoFocus}/>
            <InputLabel label={label} htmlFor={inputId} isRadioOrCheckbox={true} isDisabled={isDisabled} hasDefaultMarker={hasDefaultMarker} popover={popover} aria-describedby={[description ? `${inputId}-description` : null, context?.error ? `${context?.groupId}-error` : null].filter(Boolean).join(" ") || undefined}/>
            {description && <FormDescription id={`${inputId}-description`} description={description} isDisabled={isDisabled} isRadioOrCheckbox={true}/>}
        </div>);
}
export default RadioButtonNew;
const radioButtonSize = "1.25rem";
const radioButtonStyles = css({
    display: "grid",
    gridTemplateColumns: `${radioButtonSize} auto`,
    columnGap: space[8],
    alignItems: "baseline",
    margin: 0,
    marginBottom: space[8],
});
const radioInputStyles = css({
    margin: 0,
    appearance: "none",
    backgroundColor: themeTokens.color.background.primary.default,
    font: "inherit",
    color: "currentcolor",
    width: `${radioButtonSize}`,
    height: `${radioButtonSize}`,
    border: `${borderWidth[1]} solid ${themeTokens.color.border.bold}`,
    borderRadius: borderRadius.circle,
    transform: `translateY(-${space[1]})`,
    display: "grid",
    placeContent: "center",
    cursor: "pointer",
    "&:disabled": {
        backgroundColor: themeTokens.color.background.disabled,
        cursor: "not-allowed",
    },
    "&::before": {
        content: `""`,
        width: `calc(${radioButtonSize} / 2)`,
        height: `calc(${radioButtonSize} / 2)`,
        borderRadius: borderRadius.circle,
        transform: "scale(0)",
        transition: "100ms transform ease-out",
        boxShadow: `inset ${radioButtonSize} ${radioButtonSize} ${themeTokens.color.background.primary.default}`,
        // For windows high contrast mode
        backgroundColor: "CanvasText",
    },
    "&:checked:disabled": {
        backgroundColor: themeTokens.color.border.disabled,
        borderColor: themeTokens.color.border.tertiary,
    },
    "&:disabled:checked::before": {
        backgroundColor: themeTokens.color.background.primary.default,
        width: `calc(${radioButtonSize} / 2)`,
        height: `calc(${radioButtonSize} / 2)`,
    },
    "&:checked": {
        backgroundColor: themeTokens.color.background.selected,
        borderColor: themeTokens.color.border.selected,
    },
    "&:checked::before": {
        transform: "scale(1)",
    },
    "&:focus": {
        outline: `max(2px, 0.15em) solid ${themeTokens.color.border.selected}`,
        outlineOffset: `max(2px, 0.15em)`,
    },
});
